import React from "react";
import {
  SettingsFilterCard, SettingsSearchWrap, SettingsFormSelect, SettingsFormInput,
} from "../../Settings/SettingShell";
import TagFilterBar from "../../Settings/Components/TagFilterBar";

const VERIFIED_FILTER_OPTIONS = [
  { key: "all", label: "All", icon: "fas fa-list" },
  { key: "verified", label: "Verified", icon: "fas fa-check-circle" },
  { key: "unverified", label: "Unverified", icon: "fas fa-exclamation-circle" },
];

const VERIFIED_FILTER_ACTIVE_STYLE = {
  all: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  verified: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" },
  unverified: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
};

/** Segmented All/Verified/Unverified toggle — badge-styled to match the pills
 *  used everywhere else evidence status is shown (list rows, detail page). */
const VerifiedFilterToggle = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 4, padding: 3, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 20 }}>
    {VERIFIED_FILTER_OPTIONS.map((opt) => {
      const active = value === opt.key;
      const style = active ? VERIFIED_FILTER_ACTIVE_STYLE[opt.key] : null;
      return (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          style={{
            display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 17,
            border: `1px solid ${active ? style.border : "transparent"}`,
            background: active ? style.bg : "transparent",
            color: active ? style.color : "#64748b",
            fontSize: 11.5, fontWeight: active ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          <i className={opt.icon} />{opt.label}
        </button>
      );
    })}
  </div>
);

/**
 * One consolidated, always-visible filter area — no "Advanced Filters"
 * modal. Row 1 holds search/FY/location-ish controls (Location, Sub-location
 * and Period only apply to quantitative — qualitative evidence doesn't
 * depend on any of them). Row 2 is the tag filter, driven by the active
 * tab's reporting-questions map so it reads the same KPI tags everywhere
 * (listing here, KPI picker in the upload modal).
 */
const EvidenceFilterBar = ({
  search, onSearchChange,
  financialYears, financialYearId, onFinancialYearChange,
  isQuantitative,
  sources, sourceId, onSourceChange,
  subLocationId, onSubLocationChange,
  periodFrom, periodTo, onPeriodFromChange, onPeriodToChange,
  verifiedFilter, onVerifiedFilterChange,
  allTagQuestions, filterSteps, onFilterStepsChange,
}) => {
  const selectedSource = sources.find((s) => String(s.id) === String(sourceId));
  const subLocations = selectedSource?.subLocation || [];

  return (
    <SettingsFilterCard style={{ flexDirection: "column", alignItems: "stretch" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <SettingsSearchWrap style={{ flex: 2, minWidth: 200 }}>
          <i className="fas fa-search s-icon" aria-hidden="true" />
          <input
            type="search"
            placeholder="Search evidence by file name…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </SettingsSearchWrap>

        <div style={{ minWidth: 170 }}>
          <SettingsFormSelect value={financialYearId} onChange={(e) => onFinancialYearChange(e.target.value)} style={{ height: 38 }}>
            {financialYears.map((fy) => <option key={fy.id} value={fy.id}>{fy.financial_year_value}</option>)}
          </SettingsFormSelect>
        </div>

        {isQuantitative && (
          <>
            <div style={{ minWidth: 170 }}>
              <SettingsFormSelect value={sourceId} onChange={(e) => { onSourceChange(e.target.value); onSubLocationChange(""); }} style={{ height: 38 }}>
                <option value="">All Locations</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>{s.unitCode ? s.unitCode : `${s.location?.city} - ${s.location?.state}`}</option>
                ))}
              </SettingsFormSelect>
            </div>

            <div style={{ minWidth: 170 }}>
              <SettingsFormSelect
                value={subLocationId}
                onChange={(e) => onSubLocationChange(e.target.value)}
                style={{ height: 38 }}
                disabled={!subLocations.length}
              >
                <option value="">{subLocations.length ? "All Sub-locations" : "No sub-locations"}</option>
                {subLocations.map((sub) => (
                  <option key={sub.id} value={sub.id}>{sub.subLocation}</option>
                ))}
              </SettingsFormSelect>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>Period</span>
              <SettingsFormInput type="date" value={periodFrom} onChange={(e) => onPeriodFromChange(e.target.value)} style={{ height: 38, width: 140 }} />
              <span style={{ color: "#94a3b8" }}>–</span>
              <SettingsFormInput type="date" value={periodTo} onChange={(e) => onPeriodToChange(e.target.value)} style={{ height: 38, width: 140 }} />
            </div>

            <VerifiedFilterToggle value={verifiedFilter} onChange={onVerifiedFilterChange} />
          </>
        )}
      </div>

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
        <TagFilterBar
          allQuestions={allTagQuestions}
          filterSteps={filterSteps}
          onFilterStepsChange={onFilterStepsChange}
          pendingIds={new Set()}
        />
      </div>
    </SettingsFilterCard>
  );
};

export default EvidenceFilterBar;
